import { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  MoreHorizontal,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Database
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const STATUS_BADGES = {
  draft: { label: 'Draft', color: 'bg-gray-500' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-500' },
  published: { label: 'Published', color: 'bg-green-500' },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-500' },
  archived: { label: 'Archived', color: 'bg-slate-500' }
};

export const BlogManager = () => {
  const { token, user, isAdmin, logout } = useCMSAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    featured_image: '',
    status: 'draft'
  });

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '15' });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const res = await fetch(`${API_URL}/api/cms/content/blog?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setTotalPages(data.pages);
        setTotal(data.total);
        setCategories(data.categories || []);
      }
    } catch (e) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  }, [token, page, search, statusFilter, categoryFilter, logout]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      const url = selectedPost 
        ? `${API_URL}/api/cms/content/blog/${selectedPost.id}`
        : `${API_URL}/api/cms/content/blog`;
      
      const res = await fetch(url, {
        method: selectedPost ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(selectedPost ? 'Post updated' : 'Post created');
        setDialogOpen(false);
        resetForm();
        fetchPosts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save post');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/blog/${selectedPost.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Post deleted');
        setDeleteDialogOpen(false);
        setSelectedPost(null);
        fetchPosts();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete post');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content,
      category: post.category || '',
      tags: post.tags?.join(', ') || '',
      featured_image: post.featured_image || '',
      status: post.status
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      tags: '',
      featured_image: '',
      status: 'draft'
    });
    setSelectedPost(null);
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing blog posts with seed data. Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/blog/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Blog posts seeded successfully!');
        fetchPosts();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const canEdit = ['super_admin', 'admin', 'content_approver', 'content_editor'].includes(user?.role);
  const canDelete = ['super_admin', 'admin', 'content_approver'].includes(user?.role);
  const canApprove = ['super_admin', 'admin', 'content_approver'].includes(user?.role);

  const handleApprovalAction = async (postId, action, comment = null) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/content/blog/${postId}/approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, comment })
      });
      
      if (res.ok) {
        const actionLabels = {
          submit_for_review: 'Submitted for review',
          approve: 'Approved and published',
          reject: 'Rejected',
          request_changes: 'Changes requested'
        };
        toast.success(actionLabels[action] || 'Action completed');
        fetchPosts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (e) {
      toast.error('Failed to perform action');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="blog-manager">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ux-charcoal flex items-center gap-2">
              <FileText className="w-7 h-7 text-ux-violet" />
              Blog Posts
            </h1>
            <p className="text-ux-charcoal/60">
              Manage blog articles and content
            </p>
          </div>
          
          {canEdit && (
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={handleSeedData}
                disabled={seeding}
              >
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Seed Data
              </Button>
              <Button 
                className="bg-ux-violet hover:bg-ux-violet/90"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                data-testid="add-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ux-charcoal/40" />
                <Input
                  placeholder="Search posts..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="changes_requested">Changes Requested</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Posts Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Date</TableHead>
                  {canEdit && <TableHead className="w-16">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-ux-charcoal/60">
                      No posts found
                    </TableCell>
                  </TableRow>
                ) : (
                  posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{post.title}</p>
                          {post.excerpt && (
                            <p className="text-xs text-ux-charcoal/60 truncate max-w-xs">{post.excerpt}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{post.category || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_BADGES[post.status]?.color || 'bg-gray-500'} text-white`}>
                          {STATUS_BADGES[post.status]?.label || post.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{post.author_name}</TableCell>
                      <TableCell className="text-sm text-ux-charcoal/60">
                        {new Date(post.created_at).toLocaleDateString()}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(post)}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              
                              {/* Approval workflow actions */}
                              {(post.status === 'draft' || post.status === 'changes_requested') && (
                                <DropdownMenuItem 
                                  onClick={() => handleApprovalAction(post.id, 'submit_for_review')}
                                  className="text-blue-600"
                                >
                                  <Send className="w-4 h-4 mr-2" /> Submit for Review
                                </DropdownMenuItem>
                              )}
                              
                              {canApprove && post.status === 'pending_review' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleApprovalAction(post.id, 'approve')}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" /> Approve & Publish
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for requesting changes:');
                                      if (comment) handleApprovalAction(post.id, 'request_changes', comment);
                                    }}
                                    className="text-orange-600"
                                  >
                                    <Clock className="w-4 h-4 mr-2" /> Request Changes
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      const comment = prompt('Enter reason for rejection:');
                                      if (comment) handleApprovalAction(post.id, 'reject', comment);
                                    }}
                                    className="text-red-600"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" /> Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {canDelete && (
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedPost(post); setDeleteDialogOpen(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-ux-charcoal/60">
              Showing {((page - 1) * 15) + 1} - {Math.min(page * 15, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPost ? 'Edit Post' : 'New Blog Post'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(d => ({ ...d, title: e.target.value }))}
                  placeholder="Post title"
                />
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData(d => ({ ...d, excerpt: e.target.value }))}
                  placeholder="Brief summary (max 300 chars)"
                  rows={2}
                />
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(d => ({ ...d, content: e.target.value }))}
                  placeholder="Full post content..."
                  rows={10}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData(d => ({ ...d, category: e.target.value }))}
                    placeholder="e.g. Study Tips"
                  />
                </div>
                <div>
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData(d => ({ ...d, tags: e.target.value }))}
                    placeholder="e.g. IELTS, Study Abroad"
                  />
                </div>
              </div>
              <div>
                <Label>Featured Image URL</Label>
                <Input
                  value={formData.featured_image}
                  onChange={(e) => setFormData(d => ({ ...d, featured_image: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Status</Label>
                <div className="space-y-3 mt-2">
                  <PublishStatusCard
                    isPublished={formData.status === 'published'}
                    onChange={(v) => setFormData(d => ({ ...d, status: v ? 'published' : 'draft' }))}
                    entityLabel="blog post"
                    publicRoute="/blog"
                    testId="blog-publish-toggle"
                  />
                  <Select value={formData.status} onValueChange={(v) => setFormData(d => ({ ...d, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived (soft delete)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedPost ? 'Save Changes' : 'Create Post'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Post</DialogTitle>
            </DialogHeader>
            <p>Are you sure you want to delete "{selectedPost?.title}"?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BlogManager;
